import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useRef, useState } from 'react';

import { type ChatMessageType, type UserChatMessageType } from '@/api/ai';
import AIStarsIcon from '@/assets/svg/ai-stars.svg?react';
import { Button } from '@/components/buttons';
import { AISearchInput } from '@/components/Form';
import { Combobox, ComboboxContent, ComboboxItem, ComboboxList } from '@/components/Form/pickers';
import { useHeaderContext } from '@/hooks/header/HeaderContext';
import {
  type WebSocketAIMessage,
  type WebSocketError,
  useAuthWebSocket,
} from '@/hooks/useAuthWebSocket';
import { cn } from '@/lib/utils';

import AIDrawer from './AIDrawer';
import { type RequestStatus } from './statusState';

const AISearch = () => {
  const { exampleAIQuestions } = useHeaderContext();
  const [chatSessionId, setChatSessionId] = useState<string>();
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [isAIResponseLoading, setAIResponseLoading] = useState(false);
  const [isAIStreamingResponse, setIsAIStreamingResponse] = useState(false);
  const [requestStatus, setRequestStatus] = useState<RequestStatus | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputGroupAnchorRef = useRef<HTMLDivElement>(null);

  const handleMessageReceived = (wsMessage: WebSocketAIMessage) => {
    if (wsMessage.type === 'status') {
      setRequestStatus(wsMessage.payload);
      return;
    }

    if (wsMessage.type !== 'chunk') {
      setChatSessionId(wsMessage.payload.sessionId);
      setIsAIStreamingResponse(false);
    }

    setAIResponseLoading(false);
    setRequestStatus(undefined);
    setMessages((prevMessages) => {
      const sessionId = wsMessage.type === 'chunk' ? chatSessionId : wsMessage.payload.sessionId;

      if (!prevMessages.length) {
        return [
          {
            content: wsMessage.payload.content,
            role: 'ai',
            isComplete: wsMessage.type === 'complete',
            sessionId,
          },
        ];
      }

      const lastMessage = prevMessages[prevMessages.length - 1];

      if (lastMessage.role === 'ai' && !lastMessage.isComplete) {
        return [
          ...prevMessages.slice(0, -1),
          {
            ...lastMessage,
            content: lastMessage.content + wsMessage.payload.content,
            isComplete: wsMessage.type === 'complete',
            sessionId,
          },
        ];
      }

      return [
        ...prevMessages,
        {
          // add a space to the end in case more messages come
          content: wsMessage.payload.content + ' ',
          role: 'ai',
          isComplete: wsMessage.type === 'complete',
          sessionId,
        },
      ];
    });
  };

  const handleErrorReceived = (wsMessage: WebSocketError) => {
    setIsAIStreamingResponse(false);
    setAIResponseLoading(false);
    setRequestStatus(undefined);
    setMessages((prevMessages) => {
      const lastMessage = prevMessages[prevMessages.length - 1];
      const errorMessage: ChatMessageType = {
        content: t`An error occurred while processing your request. Please contact support if the issue persists.`,
        role: 'error',
        sessionId: wsMessage.payload.sessionId,
      };

      if (lastMessage.role === 'ai' && !lastMessage.isComplete) {
        // if we were streaming and encountered an error, replace the accumulated streamed message
        // with the error message
        return [...prevMessages.slice(0, -1), errorMessage];
      }

      return [...prevMessages, errorMessage];
    });
  };

  const { onSendMessage } = useAuthWebSocket({
    onMessageReceived: handleMessageReceived,
    onErrorReceived: handleErrorReceived,
  });

  const sendMessage = async (message: string) => {
    const userMessage: UserChatMessageType = {
      content: message,
      role: 'user',
      sessionId: chatSessionId,
    };

    setMessages((prev) => [...prev, userMessage]);
    setAIResponseLoading(true);
    setIsAIStreamingResponse(true);
    setRequestStatus(undefined);

    onSendMessage?.({
      content: message,
      sessionId: chatSessionId,
    });
  };

  const handleSearch = (value: string) => {
    setDrawerOpen(true);
    setOpen(false);
    setInputValue('');
    sendMessage(value);
  };

  const handleSendMessage = (message: string) => {
    sendMessage(message);
  };

  const hasExampleQuestions = exampleAIQuestions.length > 0;

  return (
    <div className="relative">
      {messages.length === 0 ? (
        <Combobox<string>
          items={exampleAIQuestions}
          filter={null}
          inputRef={inputRef}
          inputValue={inputValue}
          onInputValueChange={setInputValue}
          open={open && hasExampleQuestions}
          onOpenChange={setOpen}
          autoHighlight={false}
          highlightItemOnHover
          modal={false}
          onValueChange={(question) => {
            if (question) {
              handleSearch(question);
            }
          }}
        >
          <div ref={inputGroupAnchorRef}>
            <AISearchInput
              ref={inputRef}
              value={inputValue}
              onValueChange={setInputValue}
              className={cn(
                'w-100 max-xl:w-75 max-lg:w-50 max-md:hidden',
                'transition-[border-radius] delay-100 duration-100 ease-in-out',
                {
                  'rounded-b-none delay-0 duration-50': open && hasExampleQuestions,
                },
              )}
              size="medium"
              isSearchDisabled={isAIStreamingResponse}
              placeholder={t`How can I help you?`}
              autoComplete="off"
              isRound
              onSearch={handleSearch}
              onFocus={() => setOpen(true)}
              onBlur={(event) => {
                const nextElement = event.relatedTarget as HTMLElement | null;

                if (nextElement?.closest('[data-slot="combobox-content"]')) {
                  return;
                }

                setOpen(false);
              }}
            />
          </div>

          <ComboboxContent
            anchor={inputGroupAnchorRef}
            side="bottom"
            align="start"
            sideOffset={-1}
            initialFocus={false}
            finalFocus={false}
            className={cn(
              'w-100 max-xl:w-75 max-lg:w-50 max-md:hidden',
              'rounded-t-none rounded-b-2xl border border-green-500 p-1',
              'ring-1 ring-green-500/50 ring-offset-0',
            )}
          >
            <ComboboxList>
              {(question: string) => (
                <ComboboxItem
                  key={question}
                  value={question}
                  className="text-sm aria-selected:font-normal data-highlighted:not-data-disabled:bg-gray-200"
                >
                  {question}
                </ComboboxItem>
              )}
            </ComboboxList>
          </ComboboxContent>
        </Combobox>
      ) : (
        <Button className="ps-4!" onClick={() => setDrawerOpen(true)}>
          <AIStarsIcon />
          <Trans>Open Conversation</Trans>
        </Button>
      )}

      <AIDrawer
        open={isDrawerOpen}
        messages={messages}
        isAIResponseLoading={isAIResponseLoading}
        responseStatus={requestStatus}
        isAIStreamingResponse={isAIStreamingResponse}
        onClose={() => setDrawerOpen(false)}
        onSendMessage={handleSendMessage}
      />
    </div>
  );
};

export default AISearch;
